import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
  TextField,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient, setCsrfToken } from '@shared/services/ApiClient'
import { CheckCircle } from '@mui/icons-material'
import PasswordStrengthMeter from '../components/PasswordStrengthMeter'

interface AuthResponse {
  token: string | null
  refreshToken: string | null
  csrfToken: string | null
  expiresAt: string
  user: any
  requiresMfaSetup?: boolean
}

const resetPasswordSchema = yup.object({
  password: yup
    .string()
    .required('Password is required')
    .min(15, 'Password must be at least 15 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match')
})

interface ResetPasswordFormData {
  password: string
  confirmPassword: string
}

function ResetPasswordPage() {
  const theme = useTheme()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const tokenParam = searchParams.get('token')
    if (!tokenParam) {
      setError('Invalid or missing reset token. Please request a new password reset link.')
    } else {
      setToken(tokenParam)
    }
  }, [searchParams])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(resetPasswordSchema),
    mode: 'onBlur'
  })

  const password = watch('password', '')

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setError('Invalid or missing reset token.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await apiClient.post<AuthResponse>('/auth/confirm-password-reset', {
        Token: token,
        NewPassword: data.password,
        ConfirmPassword: data.confirmPassword
      })

      // Password reset auto-logs in the user - store CSRF token and redirect
      if (response.csrfToken) {
        setCsrfToken(response.csrfToken)
      }

      setSuccess(true)

      // Redirect to dashboard after brief delay
      // The auth cookies are already set by the backend
      setTimeout(() => {
        window.location.href = '/'
      }, 2000)
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. The link may have expired or already been used.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Container maxWidth="sm">
        <Paper sx={{ p: 6, borderRadius: 2 }}>
          {!success ? (
            <>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Reset Password
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Enter your new password below
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit(onSubmit)}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  autoComplete="new-password"
                  {...register('password')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  disabled={isLoading || !token}
                  sx={{ mb: 2 }}
                />
                <PasswordStrengthMeter password={password || ''} />

                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  {...register('confirmPassword')}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  disabled={isLoading || !token}
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading || !token}
                  sx={{
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    mb: 3
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Resetting Password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/login')}
                >
                  Back to Sign In
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Password Reset Successful!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Your password has been successfully reset. Logging you in...
              </Typography>
              <CircularProgress />
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  )
}

export default ResetPasswordPage
